import { FC, FormEventHandler, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Autocomplete } from '@mui/material';

import { IndexName } from '@enums/IndexName';
import { SearchContext } from '@enums/SearchContext';
import useFilterDefsQuery from '@hooks/queryHooks/useFilterDefsQuery';
import { useLocalStorage } from '@hooks/useLocalStorage';
import useMetadataContext from '@hooks/useMetadataContext';
import useSuggestAsYouType from '@hooks/useSuggestAsYouType';
import { compressFilterKeys, serialize } from '@modules/layouts/hooks/useSearchAndFilter/utils';
import { generateFilter } from '@utils/filterUtils';
import { getSearchPath } from 'src/appRoutes';

import GlobalSearchTextField from './GlobalSearchTextField';
import { ElementFocus } from './utils';

const GlobalSearchField: FC = () => {
  const navigate = useNavigate();
  const { selectTitle } = useMetadataContext();
  const { data: filterDefs } = useFilterDefsQuery(IndexName.Assets);

  const inputRef = useRef<HTMLInputElement>(null);

  const [focus, setFocus] = useState<ElementFocus>({ input: false, button: false, menu: false });
  const [queryText, setQueryText] = useState('');

  const [searchContext, setSearchContext] = useLocalStorage<SearchContext>(
    'globalSearchContext',
    SearchContext.TITLE
  );

  const hasFocus = Object.values(focus).some(Boolean);

  const { suggestions, loading } = useSuggestAsYouType(queryText, {
    disabled: !(focus.input && searchContext === SearchContext.TITLE),
    minLength: 3,
  });

  const handleSubmit = (value: string | undefined = queryText) => {
    switch (searchContext) {
      case SearchContext.TITLE:
        navigate(getSearchPath(IndexName.Assets, { q: value }));
        setQueryText('');
        inputRef.current?.blur();
        break;

      case SearchContext.MASTER_ID:
        selectTitle({ masterId: value });
        break;

      case SearchContext.PLATFORM_TITLE_ID:
        selectTitle({ platformTitleId: value });
        break;

      case SearchContext.PROVIDER_TITLE_ID: {
        if (filterDefs) {
          const filter = generateFilter(filterDefs, 'ProviderTitleId', [value], true, 'or');
          if (filter) {
            navigate(
              getSearchPath(IndexName.Assets, { filter: serialize(compressFilterKeys(filter)) })
            );
            setQueryText('');
            inputRef.current?.blur();
          }
        }
        break;
      }
    }
  };

  const onSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    handleSubmit();
  };

  const setElementFocus = (element: keyof ElementFocus, focused: boolean) =>
    setFocus(prev => ({ ...prev, [element]: focused }));

  return (
    <form onSubmit={onSubmit}>
      <Autocomplete
        disableClearable
        disablePortal
        freeSolo
        fullWidth
        loading={loading}
        onBlur={() => setElementFocus('input', false)}
        onChange={(_, val, reason) => {
          if (reason === 'selectOption' && val) handleSubmit(val);
          else if (val) setQueryText(val);
          else setQueryText('');
        }}
        onFocus={() => setElementFocus('input', true)}
        options={searchContext === SearchContext.TITLE ? suggestions : []}
        renderInput={({ InputProps, InputLabelProps: _InputLabelProps, ...rest }) => (
          <GlobalSearchTextField
            {...rest}
            hasFocus={hasFocus}
            queryText={queryText}
            ref={inputRef}
            searchContext={searchContext}
            setElementFocus={setElementFocus}
            setQueryText={setQueryText}
            setSearchContext={setSearchContext}
            slotProps={{ input: InputProps }}
          />
        )}
        value={queryText}
      />
    </form>
  );
};

export default GlobalSearchField;
